/**
 * Sub-components used by AdminUsersTab.js. Split out purely to keep
 * AdminUsersTab.js under the 500-line file budget — no behaviour change.
 */
import React, { useState } from 'react';
import { ShieldCheck, Loader2, Filter } from 'lucide-react';
import { PaginationControls } from './AdminTableUtils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

const RATE_OPTIONS = [
  { value: '', label: 'No discount' },
  { value: 'advisor_partner', label: 'Partner Rate (20% off)' },
  { value: 'advisor_client', label: 'Preferred Client Rate (10% off)' },
];

const RATE_BADGE = {
  advisor_partner: { label: 'Partner', cls: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' },
  advisor_client: { label: 'Preferred', cls: 'bg-amber-500/20 text-amber-400 border border-amber-500/50' },
};

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export function UserRowSkeleton({ index }) {
  return (
    <tr className="border-b border-gray-700/50" data-testid={`user-row-skeleton-${index}`}>
      {Array.from({ length: 9 }).map((_, i) => (
        <td key={i} className="py-3 px-4"><div className="h-4 bg-gray-700/50 rounded animate-pulse" /></td>
      ))}
    </tr>
  );
}

export function RateControl({ user, onChange }) {
  const [saving, setSaving] = useState(false);
  const current = user.discount_type || '';
  const badge = RATE_BADGE[current];

  const apply = async (next) => {
    if ((next || '') === current) return;
    if (!window.confirm(`Set ${user.email} → ${next ? RATE_OPTIONS.find(o => o.value === next)?.label : 'No discount'}?`)) return;
    setSaving(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/users/${user.user_id}/discount-type`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ discount_type: next ? next : null }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (${resp.status})`);
      }
      toast.success(`Rate updated`);
      onChange?.(user.user_id, next || null);
    } catch (e) {
      toast.error(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {badge ? (
        <span className={`px-2 py-0.5 rounded text-xs ${badge.cls}`} data-testid={`rate-badge-${user.user_id}`}>{badge.label}</span>
      ) : (
        <span className="text-xs text-gray-500" data-testid={`rate-badge-${user.user_id}`}>—</span>
      )}
      <select
        value={current} disabled={saving}
        onChange={(e) => apply(e.target.value)}
        className="bg-gray-700 border border-gray-600 text-xs rounded px-1.5 py-0.5 text-gray-200 focus:outline-hidden focus:border-emerald-500"
        data-testid={`rate-select-${user.user_id}`} title="Set the user's subscription rate"
      >
        {RATE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {saving && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
    </div>
  );
}

const TIER_OPTIONS = [
  { value: 'premium_portfolio', label: 'Portfolio', cls: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50' },
  { value: 'premium_continuity', label: 'Continuity (legacy)', cls: 'bg-blue-500/20 text-blue-400 border border-blue-500/50' },
  { value: 'portfolio_continuity', label: 'Gateway Continuity', cls: 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/50' },
];

const VOLUME_BUCKET_META = {
  no_portfolio: { label: 'None', cls: 'bg-gray-700/40 text-gray-400 border border-gray-600' },
  under_100k:   { label: '<$100k', cls: 'bg-sky-500/20 text-sky-300 border border-sky-500/40' },
  '100k_500k':  { label: '$100k-500k', cls: 'bg-teal-500/20 text-teal-300 border border-teal-500/40' },
  '500k_1m':    { label: '$500k-1M', cls: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' },
  '1m_5m':      { label: '$1M-5M', cls: 'bg-amber-500/20 text-amber-300 border border-amber-500/40' },
  over_5m:      { label: '>$5M', cls: 'bg-rose-500/20 text-rose-300 border border-rose-500/40' },
};

function VolumeBadge({ user }) {
  const key = user.portfolio_value_bucket || 'no_portfolio';
  const meta = VOLUME_BUCKET_META[key] || VOLUME_BUCKET_META.no_portfolio;
  const rawUsd = user.portfolio_value_usd; // Owner-only; stripped for non-owners at API layer
  const hasUsd = typeof rawUsd === 'number' && rawUsd > 0;

  // Owner path: they have the privilege of the exact number — show it as the
  // primary visual. Bucket is redundant for them so we omit it (avoids the
  // eye latching on to the loud badge and missing the number).
  if (hasUsd) {
    return (
      <span
        className="text-sm font-semibold text-white tabular-nums whitespace-nowrap"
        data-testid={`volume-usd-${user.user_id}`}
        title="Actual portfolio value (Owner view)"
      >
        ${Math.round(rawUsd).toLocaleString()}
      </span>
    );
  }
  // Non-owner path (or user with no portfolio): show the anonymised bucket badge.
  return (
    <span
      className={`px-2 py-0.5 rounded text-[10px] whitespace-nowrap ${meta.cls}`}
      data-testid={`volume-badge-${user.user_id}`}
      title="Anonymised portfolio-volume bucket"
    >
      {meta.label}
    </span>
  );
}

export function TierControl({ user, onChange }) {
  const [saving, setSaving] = useState(false);
  const current = user.tier || 'premium_portfolio';
  const currentOpt = TIER_OPTIONS.find(o => o.value === current) || TIER_OPTIONS[0];

  const apply = async (next) => {
    if (next === current) return;
    const label = TIER_OPTIONS.find(o => o.value === next)?.label || next;
    if (!window.confirm(`Change ${user.email} → Premium ${label}?\n\nThis is logged in the audit trail.`)) return;
    setSaving(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/users/${user.user_id}/tier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ tier: next, reason: 'admin_ui' }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (${resp.status})`);
      }
      toast.success('Tier updated');
      onChange?.(user.user_id, next);
    } catch (e) {
      toast.error(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className={`px-2 py-0.5 rounded text-xs ${currentOpt.cls}`} data-testid={`tier-badge-${user.user_id}`}>{currentOpt.label}</span>
      <select
        value={current} disabled={saving}
        onChange={(e) => apply(e.target.value)}
        className="bg-gray-700 border border-gray-600 text-xs rounded px-1.5 py-0.5 text-gray-200 focus:outline-hidden focus:border-emerald-500"
        data-testid={`tier-select-${user.user_id}`} title="Change user tier (audit-logged)"
      >
        {TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {saving && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
    </div>
  );
}

export function UsersTable({ users, onDeleteUser, checking, onRunCheck, onRateChange, onTierChange }) {
  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-gray-700">
          {['Email', 'Name', 'Auth Type', 'Tier', 'Country', 'Volume', 'Rate', 'Created', 'Actions'].map(h => (
            <th key={h} className={`${h === 'Actions' ? 'text-right' : 'text-left'} py-3 px-4 text-gray-300 font-medium`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {users.map((user, idx) => (
          <tr key={`${user.user_id}-${idx}`} className="border-b border-gray-700/50 hover:bg-gray-700/30" data-testid={`user-row-${user.user_id}`}>
            <td className="py-3 px-4 text-white break-all">{user.email}</td>
            <td className="py-3 px-4 text-gray-300">{user.name}</td>
            <td className="py-3 px-4">
              <span className={`px-2 py-1 rounded text-xs ${user.auth_type === 'google' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-600/50 text-gray-300'}`}>
                {user.auth_type === 'google' ? 'Google' : 'Email'}
              </span>
            </td>
            <td className="py-3 px-4">
              <TierControl user={user} onChange={onTierChange} />
            </td>
            <td className="py-3 px-4 text-gray-300">{user.country || 'N/A'}</td>
            <td className="py-3 px-4"><VolumeBadge user={user} /></td>
            <td className="py-3 px-4"><RateControl user={user} onChange={onRateChange} /></td>
            <td className="py-3 px-4 text-gray-400 text-sm">{formatDate(user.created_at)}</td>
            <td className="py-3 px-4 text-right">
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => onRunCheck(user.user_id)} disabled={checking === user.user_id}
                  className="px-3 py-1 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 rounded-lg text-sm font-medium transition border border-amber-600/50 flex items-center gap-1"
                  data-testid={`integrity-check-${user.user_id}`} title="Run data integrity check"
                >
                  {checking === user.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline">Check</span>
                </button>
                <button
                  onClick={() => onDeleteUser(user.email)}
                  className="px-3 py-1 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm font-medium transition border border-red-600/50"
                  data-testid={`delete-user-${user.user_id}`}
                >Delete</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AdvancedFiltersPanel({
  advancedOpen, setAdvancedOpen, advancedActiveCount,
  accountAgeBucket, setAccountAgeBucket,
  lastLoginWithinDays, setLastLoginWithinDays,
  activityStatus, setActivityStatus,
  portfolioVolume, setPortfolioVolume,
}) {
  return (
    <details open={advancedOpen} onToggle={(e) => setAdvancedOpen(e.currentTarget.open)} className="mb-4 rounded-lg border border-gray-700 bg-gray-900/30" data-testid="admin-users-advanced-filters">
      <summary className="px-3 py-2 cursor-pointer text-xs text-gray-300 hover:bg-gray-700/30 select-none inline-flex items-center gap-2 w-full">
        <Filter className="w-3.5 h-3.5" />
        <span className="font-medium">Advanced filters</span>
        {advancedActiveCount > 0 && (
          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 text-[10px] font-semibold" data-testid="advanced-filters-active-count">{advancedActiveCount} active</span>
        )}
        <span className="ml-auto text-[10px] text-gray-500">{advancedOpen ? 'click to collapse' : 'click to expand'}</span>
      </summary>
      <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-[10px] text-gray-400 mb-1 uppercase tracking-wide">Account Age</label>
          <select value={accountAgeBucket} onChange={(e) => setAccountAgeBucket(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500" data-testid="admin-users-filter-account-age">
            <option value="">Any age</option>
            <option value="under_90">Less than 90 days</option>
            <option value="91_180">91-180 days</option>
            <option value="181_365">181-365 days</option>
            <option value="over_365">Over 1 year</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-gray-400 mb-1 uppercase tracking-wide">Last Login</label>
          <select value={lastLoginWithinDays} onChange={(e) => setLastLoginWithinDays(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500" data-testid="admin-users-filter-last-login">
            <option value="">Anytime</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-gray-400 mb-1 uppercase tracking-wide">Activity Status</label>
          <select value={activityStatus} onChange={(e) => setActivityStatus(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500" data-testid="admin-users-filter-activity-status">
            <option value="">All</option>
            <option value="active">Active (login &lt; 180 days)</option>
            <option value="inactive">Inactive (181-365 days)</option>
            <option value="dormant">Dormant (&gt; 365 days or never)</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-gray-400 mb-1 uppercase tracking-wide">Portfolio Volume</label>
          <select value={portfolioVolume} onChange={(e) => setPortfolioVolume(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500" data-testid="admin-users-filter-portfolio-volume" title="Refreshed weekly on Sunday 04:00 UTC">
            <option value="">All portfolios</option>
            <option value="no_portfolio">No portfolio</option>
            <option value="under_100k">Under $100k</option>
            <option value="100k_500k">$100k-$500k</option>
            <option value="500k_1m">$500k-$1M</option>
            <option value="1m_5m">$1M-$5M</option>
            <option value="over_5m">Over $5M</option>
          </select>
        </div>
      </div>
    </details>
  );
}

export function UsersResultsPane({ loading, users, error, hasAnyFilter, page, total, setPage, onDeleteUser, checking, runCheck, setUsers, handleReset, load, showEmptyState }) {
  if (error) {
    return (
      <div className="text-center py-12" data-testid="admin-users-error">
        <p className="text-red-400 mb-2">{error}</p>
        <button onClick={load} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg" data-testid="admin-users-retry">Retry</button>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <UsersTable
        users={loading ? [] : users}
        onDeleteUser={onDeleteUser} checking={checking} onRunCheck={runCheck}
        onRateChange={(userId, next) => setUsers((prev) => prev.map((u) => u.user_id === userId ? { ...u, discount_type: next } : u))}
        onTierChange={(userId, next) => setUsers((prev) => prev.map((u) => u.user_id === userId ? { ...u, tier: next } : u))}
      />
      {loading && (
        <table className="w-full" data-testid="admin-users-skeleton"><tbody>
          {Array.from({ length: 5 }).map((_, i) => <UserRowSkeleton key={i} index={i} />)}
        </tbody></table>
      )}
      {showEmptyState && (
        <div className="text-center py-12" data-testid="admin-users-empty">
          <p className="text-gray-400">{hasAnyFilter ? 'No users match your filters' : 'No users found'}</p>
          {hasAnyFilter && (
            <button onClick={handleReset} className="mt-3 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg" data-testid="admin-users-clear-filters">Clear filters</button>
          )}
        </div>
      )}
      <PaginationControls currentPage={page} totalItems={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
    </div>
  );
}
